import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import fotoLixando from '../../assets/fabrica-lixando.jpg';
import { useAuth } from '../../context/AuthContext';

/* ⚠ OS NOMES VÊM DO MANUAL (`02-portais-e-acessos.md`), não da minha
   cabeça: SGQ Gestão, SGQ RH, Administração Geral. Eu escrevi "Gestão
   da produção" e "Gestão de pessoas" como nomes das metades e o dono do
   produto corrigiu — "Gestão de pessoas" é a DESCRIÇÃO do SGQ RH, na
   coluna "o que se faz", não o nome dele.

   Numa tela de escolha de portal, chamar a porta por um nome que ela
   não tem é o pior lugar possível para esse erro: é justamente onde a
   pessoa precisa RECONHECER para onde vai. */
const PORTAS = {
  sgq: {
    nome: 'SGQ Gestão',
    desc: 'Operar a fábrica — qualidade, SST, expedição, fornecedores',
    rotulo: 'Entrar no SGQ Gestão',
    destino: '/dashboard',
    icone: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7l9-4 9 4v10l-9 4-9-4z" /><path d="M3 7l9 4 9-4" /><path d="M12 11v10" />
      </svg>
    ),
  },
  rh: {
    nome: 'SGQ RH',
    desc: 'Gestão de pessoas — cargos e salários, avaliação, recrutamento',
    rotulo: 'Entrar no RH',
    destino: '/rh/dashboard',
    icone: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="8" r="3.2" /><path d="M3 20a6 6 0 0 1 12 0" />
        <circle cx="17.5" cy="9.5" r="2.4" /><path d="M15 20c0-2.6 1.6-4.3 3.5-4.3S22 17.4 22 20" />
      </svg>
    ),
  },
  geral: {
    nome: 'Administração Geral',
    desc: 'Empresas, permissões e estrutura do sistema',
    rotulo: 'Entrar na Administração Geral',
    destino: '/administracao-geral',
    icone: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 20V8l8-4 8 4v12" /><path d="M9 20v-6h6v6" />
      </svg>
    ),
  },
};

export default function SeletorPortal() {
  const { podeAcessarSGQ, podeAcessarRH, podeAcessarGeral, acessosExtrasProntos, usuario, logout } = useAuth();
  const navigate = useNavigate();

  // Precisa de pelo menos 2 portas pra a escolha fazer sentido — só
  // RH (sem Geral) já tratava isso; Geral entra na mesma conta
  // (09/08/2026), sem mudar a regra: 1 porta não pergunta nada.
  //
  // Achado real ao testar num navegador de verdade (09/08/2026): sem
  // esperar acessosExtrasProntos, esse componente montava logo depois
  // do reload do Login.jsx e via podeAcessarRH/Geral ainda como
  // 'false' (valor inicial, antes da checagem assíncrona responder) —
  // mandava todo mundo de volta pro /dashboard na hora, mesmo quem
  // tinha mais de uma porta de verdade. Essa é a MESMA corrida já
  // corrigida no RotaPublica (App.jsx), só que se manifestando aqui
  // também, num componente diferente.
  //
  // Segundo achado real, mais sério (mesma data, pergunta do Juan):
  // "SGQ" sempre contava como porta garantida (`1 +` fixo) — nunca
  // verificava se a pessoa TEM de verdade algum acesso lá. Master RH/
  // Geral (que nascem sem nenhum vínculo do lado SGQ, confirmado com
  // HTTP real: master:false, todo nível 'nenhum') viam "SGQ" como
  // opção clicável levando a um dashboard vazio. `podeAcessarSGQ`
  // (mesmo endpoint tenho-acesso das outras duas) resolve isso — e o
  // fallback de "só 1 porta" agora manda pra ONDE essa porta de
  // verdade é, não sempre pro SGQ.
  /* A sequência inteira da entrada é escura — ver a nota no `Login.jsx`.
     A classe também pinta o fundo do documento, que é o que fecha a
     faixa vazia deixada pelo `zoom` da raiz sobre o `100vh`. */
  useEffect(() => {
    document.body.classList.add('tela-entrada');
    const anterior = document.documentElement.dataset.tema;
    document.documentElement.dataset.tema = 'dark';
    return () => {
      document.body.classList.remove('tela-entrada');
      if (anterior) document.documentElement.dataset.tema = anterior;
    };
  }, []);

  const foto = { '--foto-pc': `url(${fotoLixando})`, '--foto-cel': `url(${fotoLixando})` };

  /* ESTADO QUE FALTAVA (12/08/2026): antes era `return null` aqui — a
     pessoa via TELA EM BRANCO entre o login e o seletor enquanto a
     checagem de acessos não respondia. Em link lento é meio segundo de
     nada, e o histórico deste arquivo mostra que essa corrida já causou
     bug antes. Agora ela vê a pergunta e dois esqueletos.

     ⚠ E O ESQUELETO MORA NA MESMA CENA DA RESPOSTA (10/09/2026). Ele
     usava `.en-cena` solta, sem painel nem caixa — a tela piscava de um
     arranjo para outro no meio do carregamento. Duas telas para o mesmo
     momento é o defeito; a espera tem de ter a forma do que vem. */
  const marcaETopo = (
    <div className="en-topo">
      <span className="en-marca">SGQ.</span>
      {/* ⚠ A TELA PASSOU A DIZER QUEM ESTÁ ENTRANDO, E A OFERECER SAÍDA
          (11/09/2026). Era a única do sistema que perguntava para onde
          ir sem dizer para quem — e em aparelho compartilhado de fábrica
          quem caiu aqui por engano não tinha como voltar.

          ⚠ E ISSO É PEÇA PRETA PORQUE SE TOCA. A regra das três telas é
          "preto cheio = clicável"; o "sair" é um botão de verdade. */}
      <button type="button" className="en-placa en-quem" onClick={logout}>
        Você entrou como <b>{usuario?.nome?.split(' ')[0] || usuario?.usuario}</b> · sair
      </button>
    </div>
  );

  if (!acessosExtrasProntos) {
    return (
      <div className="en-cena en-portal">
        <div className="en-foto" style={foto} />
        {marcaETopo}
        <div className="en-portas">
          <div className="en-porta"><div className="en-porta-cx"><span className="en-esqueleto" /></div></div>
          <div className="en-porta"><div className="en-porta-cx"><span className="en-esqueleto" /></div></div>
        </div>
      </div>
    );
  }

  const disponiveis = [];
  if (podeAcessarSGQ) disponiveis.push('sgq');
  if (podeAcessarRH) disponiveis.push('rh');
  if (podeAcessarGeral) disponiveis.push('geral');

  if (disponiveis.length < 2) {
    navigate(PORTAS[disponiveis[0]]?.destino || '/dashboard', { replace: true });
    return null;
  }

  /* ⚠ A COSTURA É PARA A TERCEIRA, NÃO PARA A MENOS USADA. A Administração
     Geral vai para lá porque ela SOBRA da divisão em duas — e quando ela
     não sobra (quem tem Geral mas não tem SGQ ou não tem RH), ela vira
     metade como qualquer outra. Tela dividida ao meio com uma metade só
     seria a divisão mentindo sobre o que existe. */
  const metades = disponiveis.filter(k => k !== 'geral');
  const naCostura = disponiveis.includes('geral') && metades.length >= 2;
  if (!naCostura && disponiveis.includes('geral')) metades.push('geral');

  return (
    <div className="en-cena en-portal">
      <div className="en-foto" style={foto} />
      {marcaETopo}

      <div className="en-portas en-surge">
        {metades.map(chave => {
          const porta = PORTAS[chave];
          return (
            <div className="en-porta" key={chave}>
              <div className="en-porta-cx">
                <span className="en-porta-ic">{porta.icone}</span>
                <span className="en-porta-nome">{porta.nome}</span>
                <span className="en-porta-desc">{porta.desc}</span>
                <button type="button" className="en-bt en-vai" onClick={() => navigate(porta.destino)}>
                  {porta.rotulo}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {naCostura && (
        <button type="button" className="en-placa en-terceira" onClick={() => navigate(PORTAS.geral.destino)}>
          ou <b>{PORTAS.geral.nome}</b>
        </button>
      )}
    </div>
  );
}
